import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  cancelOfficeToolJob,
  createOfficeToolJob,
  downloadOfficeToolResult,
  formatOfficeBytes,
  listOfficeToolJobs,
} from "@/lib/officeTools";

const TOOL_META = {
  doctor: { label: "System diagnostic", min: 0, max: 0 },
  inspect: { label: "Inspect file", min: 1, max: 1, accept: ".docx,.xlsx,.pptx,.pdf,.png,.jpg,.jpeg,.webp,.mp4,.mov,.webm,.wav,.mp3,.m4a" },
  "pdf-render": { label: "PDF to images", min: 1, max: 1, accept: ".pdf", pages: "range" },
  "pdf-merge": { label: "Merge PDFs", min: 2, max: 5, accept: ".pdf" },
  "pdf-split": { label: "Extract PDF pages", min: 1, max: 1, accept: ".pdf", pages: "list" },
  "pdf-to-word": { label: "PDF to Word", min: 1, max: 1, accept: ".pdf" },
  "word-to-pdf": { label: "Word to PDF", min: 1, max: 1, accept: ".docx" },
  "image-to-table": { label: "Image to Excel table", min: 1, max: 1, accept: ".png,.jpg,.jpeg,.webp" },
  "excel-edit": { label: "Edit Excel with AI", min: 1, max: 1, accept: ".xlsx", instructions: true },
  "word-edit": { label: "Edit Word with AI", min: 1, max: 1, accept: ".docx", instructions: true },
  "document-draft": { label: "Draft or rewrite document", min: 0, max: 0, draft: true, instructions: true },
  "pdf-extract-text": { label: "Extract PDF text", min: 1, max: 1, accept: ".pdf" },
  "ocr-image": { label: "OCR Arabic / English", min: 1, max: 1, accept: ".png,.jpg,.jpeg,.webp", ocr: true },
  "office-to-pdf": { label: "Office file to PDF", min: 1, max: 1, accept: ".docx,.xlsx,.pptx" },
  "image-preprocess": { label: "Improve image for OCR", min: 1, max: 1, accept: ".png,.jpg,.jpeg,.webp" },
  "media-probe": { label: "Inspect audio / video", min: 1, max: 1, accept: ".mp4,.mov,.webm,.wav,.mp3,.m4a" },
  "media-extract-audio": { label: "Extract WAV audio", min: 1, max: 1, accept: ".mp4,.mov,.webm,.wav,.mp3,.m4a" },
};

const STATUS_TONE = {
  queued: "bg-amber-50 text-amber-800",
  running: "bg-blue-50 text-blue-800",
  completed: "bg-emerald-50 text-emerald-800",
  failed: "bg-red-50 text-red-800",
  cancelled: "bg-neutral-100 text-neutral-700",
};

function buildOptions(command, form) {
  if (command === "pdf-render") {
    return { first: Number(form.first || 1), last: Number(form.last || 1), dpi: Number(form.dpi || 150) };
  }
  if (command === "pdf-split") {
    return { pages: String(form.pages || "1").split(",").map((item) => Number(item.trim())).filter(Number.isInteger) };
  }
  if (command === "excel-edit" || command === "word-edit") {
    return { instructions: form.instructions, model: "qwen3.5:9b" };
  }
  if (command === "image-to-table") return { model: "qwen3.5:9b" };
  if (command === "document-draft") {
    return {
      document_type: form.documentType,
      mode: form.mode,
      instructions: form.instructions,
      source_text: form.mode === "create" ? "" : form.sourceText,
      model: "qwen3.5:9b",
    };
  }
  if (command === "ocr-image") return { language: form.language || "ara+eng", psm: 6, minimum_chars: 20 };
  if (command === "pdf-extract-text") return { minimum_chars: 1 };
  return {};
}

export default function OfficeToolRunner({ workspaceId, capabilities }) {
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const commands = useMemo(
    () => capabilities?.processing_commands || [],
    [capabilities?.processing_commands]
  );
  const [command, setCommand] = useState(commands[0] || "");
  const [files, setFiles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    instructions: "", sourceText: "", documentType: "letter", mode: "create",
    first: 1, last: 1, dpi: 150, pages: "1", language: "ara+eng",
  });

  useEffect(() => {
    if (!commands.includes(command)) setCommand(commands[0] || "");
  }, [commands, command]);

  const refreshJobs = useCallback(async () => {
    if (!workspaceId || !capabilities?.processing) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setJobs(await listOfficeToolJobs(workspaceId, controller.signal));
    } catch (error) {
      if (error?.code !== "ERR_CANCELED") toast.error("Could not load processing jobs");
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  }, [workspaceId, capabilities?.processing]);

  useEffect(() => {
    refreshJobs();
    const timer = window.setInterval(refreshJobs, 3000);
    return () => {
      window.clearInterval(timer);
      abortRef.current?.abort();
    };
  }, [refreshJobs]);

  const meta = useMemo(() => TOOL_META[command] || null, [command]);

  const submit = async (event) => {
    event.preventDefault();
    if (!meta || files.length < meta.min || files.length > meta.max) {
      toast.error(`Select ${meta?.min || 0}${meta?.max > meta?.min ? `–${meta.max}` : ""} input file(s)`);
      return;
    }
    if (meta.instructions && !form.instructions.trim()) {
      toast.error("Instructions are required");
      return;
    }
    if (meta.draft && form.mode !== "create" && !form.sourceText.trim()) {
      toast.error("Source text is required for rewrite and proofread modes");
      return;
    }
    setSubmitting(true);
    try {
      const created = await createOfficeToolJob(
        workspaceId, command, buildOptions(command, form), files
      );
      setJobs((current) => [created, ...current.filter((item) => item.job_id !== created.job_id)]);
      setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Office job queued securely");
    } catch (error) {
      toast.error(error?.response?.data?.detail?.message || "Office job could not be queued");
    } finally {
      setSubmitting(false);
    }
  };

  if (!capabilities?.processing) {
    return (
      <section className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        File processing tools are not enabled yet.
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="office-tool-runner">
      <div>
        <h2 className="text-lg font-semibold">Processing tools</h2>
        <p className="text-sm text-neutral-500">Run secure conversions, extraction and AI-assisted edits.</p>
      </div>
      <form onSubmit={submit} className="rounded-lg border border-black/10 p-4 grid gap-4">
        <label className="text-sm font-medium">Tool
          <select value={command} onChange={(event) => { setCommand(event.target.value); setFiles([]); if (fileRef.current) fileRef.current.value = ""; }} className="mt-1 h-10 w-full rounded-md border border-black/15 bg-white px-3">
            {commands.map((name) => <option key={name} value={name}>{TOOL_META[name]?.label || name}</option>)}
          </select>
        </label>

        {meta?.max > 0 && (
          <label className="text-sm font-medium">Input file{meta.max > 1 ? "s" : ""}
            <Input ref={fileRef} type="file" accept={meta.accept} multiple={meta.max > 1} onChange={(event) => setFiles(Array.from(event.target.files || []))} className="mt-1" />
            <span className="mt-1 block text-xs text-neutral-500">Required: {meta.min}{meta.max > meta.min ? `–${meta.max}` : ""} file(s)</span>
          </label>
        )}

        {meta?.pages === "range" && <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">First page<Input type="number" min="1" value={form.first} onChange={(event) => setForm({ ...form, first: event.target.value })} /></label>
          <label className="text-sm">Last page<Input type="number" min="1" value={form.last} onChange={(event) => setForm({ ...form, last: event.target.value })} /></label>
          <label className="text-sm">DPI<Input type="number" min="72" max="600" value={form.dpi} onChange={(event) => setForm({ ...form, dpi: event.target.value })} /></label>
        </div>}

        {meta?.pages === "list" && <label className="text-sm font-medium">Pages
          <Input value={form.pages} onChange={(event) => setForm({ ...form, pages: event.target.value })} placeholder="1,2,5" className="mt-1" />
        </label>}

        {meta?.draft && <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm">Document type<select value={form.documentType} onChange={(event) => setForm({ ...form, documentType: event.target.value })} className="mt-1 h-10 w-full rounded-md border border-black/15 px-3"><option value="letter">Letter</option><option value="report">Report</option></select></label>
          <label className="text-sm">Mode<select value={form.mode} onChange={(event) => setForm({ ...form, mode: event.target.value })} className="mt-1 h-10 w-full rounded-md border border-black/15 px-3"><option value="create">Create</option><option value="rewrite">Rewrite</option><option value="proofread">Proofread</option></select></label>
        </div>}

        {meta?.instructions && <label className="text-sm font-medium">Instructions
          <textarea value={form.instructions} onChange={(event) => setForm({ ...form, instructions: event.target.value })} maxLength={12000} rows={4} className="mt-1 w-full rounded-md border border-black/15 p-3" />
        </label>}

        {meta?.draft && form.mode !== "create" && <label className="text-sm font-medium">Source text
          <textarea value={form.sourceText} onChange={(event) => setForm({ ...form, sourceText: event.target.value })} maxLength={50000} rows={6} className="mt-1 w-full rounded-md border border-black/15 p-3" />
        </label>}

        {meta?.ocr && <label className="text-sm font-medium">OCR languages
          <Input value={form.language} onChange={(event) => setForm({ ...form, language: event.target.value })} className="mt-1" />
        </label>}

        <Button type="submit" disabled={submitting || !command}>{submitting ? "Queuing…" : "Run tool"}</Button>
      </form>

      <div className="grid gap-3">
        {jobs.slice(0, 20).map((job) => (
          <article key={job.job_id} className="rounded-lg border border-black/10 p-4" data-testid="office-tool-job">
            <div className="flex flex-wrap items-center gap-2">
              <div className="font-medium flex-1">{TOOL_META[job.command]?.label || job.command}</div>
              <span className={`rounded px-2 py-1 text-xs font-medium ${STATUS_TONE[job.status] || STATUS_TONE.cancelled}`}>{job.status}</span>
            </div>
            {job.error_code && <div className="mt-2 text-sm text-red-700">Error: {job.error_code}</div>}
            {job.inputs?.length > 0 && <div className="mt-2 text-xs text-neutral-500">{job.inputs.map((item) => item.original_filename).join(" · ")}</div>}
            {job.results?.length > 0 && <div className="mt-3 flex flex-wrap gap-2">
              {job.results.map((result) => <Button key={result.index} type="button" variant="outline" className="h-9" onClick={() => downloadOfficeToolResult(workspaceId, job.job_id, result).catch(() => toast.error("Result download failed"))}>Download {result.filename} · {formatOfficeBytes(result.size_bytes)}</Button>)}
            </div>}
            {job.status !== "running" && <Button type="button" variant="outline" className="mt-3 h-9 text-red-700" onClick={() => cancelOfficeToolJob(workspaceId, job.job_id).then(refreshJobs).catch(() => toast.error("Job could not be removed"))}>{job.status === "queued" ? "Cancel" : "Delete"}</Button>}
          </article>
        ))}
      </div>
    </section>
  );
}
